import { useState } from 'react';

import { AccountDropAbout } from '@playbooks/components/account/account-drop-about';
import { AccountDropAppearance } from '@playbooks/components/account/account-drop-appearance';
import { AccountDropBtn } from '@playbooks/components/account/account-drop-btn';
import { AccountDropCommunity } from '@playbooks/components/account/account-drop-community';
import { AccountDropDevelopers } from '@playbooks/components/account/account-drop-developers';
import { AccountDropResources } from '@playbooks/components/account/account-drop-resources';
import { AccountDropSelect } from '@playbooks/components/account/account-drop-select';
import { AccountDropSupport } from '@playbooks/components/account/account-drop-support';
import { GuestDropAbout } from '@playbooks/components/guest/guest-drop-about';
import { useSession, useWorkspace } from '@playbooks/contexts';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
	DropdownMenuView,
} from '@playbooks/ui/components/dropdown-menu';
import { cn } from '@playbooks/ui/lib/utils';
import { getSubscriptionUsage } from '@playbooks/utils';

const WorkspaceMenu = ({
	target = '',
	side = 'top',
	collapsed = false,
	density = 'default',
	showChevron = true,
	triggerClassName = '',
	open: controlledOpen = undefined,
	onOpenChange = undefined,
	onCreateWorkspace = undefined,
	className = '',
}) => {
	const [localOpen, setLocalOpen] = useState(false);
	const [view, setView] = useState('about');
	const [direction, setDirection] = useState<'forward' | 'back'>('forward');
	const workspace = useWorkspace();
	const session = useSession();

	// Computed
	const controlled = typeof controlledOpen === 'boolean';
	const open = controlled ? controlledOpen : localOpen;
	const profileHref = workspace.workspace.appPath || workspace.rootLink || '/';
	const usage = getSubscriptionUsage(workspace.workspace.subscription, { isFree: workspace.workspace.isFree });
	const status = usage.isDepleted ? 'danger' : usage.isOverage ? 'warning' : null;

	// Methods
	const setOpen = nextOpen => {
		if (!controlled) setLocalOpen(nextOpen);
		onOpenChange?.(nextOpen);
	};
	const onClose = () => setOpen(false);
	const onMenuOpenChange = nextOpen => {
		if (nextOpen) setView('about');
		setOpen(nextOpen);
	};
	const onView = view => {
		setDirection('forward');
		setView(view);
	};
	const onBack = () => {
		setDirection('back');
		setView('about');
	};
	const onLogout = () => {
		onClose();
		session.onLogout();
	};
	const onSelect = (type, nextWorkspace) => {
		workspace.onSwitch(type, nextWorkspace);
		onClose();
	};
	const onCreate = () => {
		onClose();
		onCreateWorkspace?.();
	};

	// Theme
	const rootClassName = cn('min-h-96 max-h-[calc(100vh-1rem)] w-80 overflow-y-auto p-0', className);

	// Props
	const props = {
		account: workspace.workspace,
		accountType: workspace.type,
		profileHref,
		session,
		target,
		onLogout,
		onSelect,
		onView,
	};

	// Render
	return (
		<DropdownMenu open={open} onOpenChange={onMenuOpenChange}>
			<DropdownMenuTrigger
				render={
					workspace.workspace.id ? (
						<AccountDropBtn
							account={workspace.workspace}
							active={open}
							status={status}
							collapsed={collapsed}
							density={density}
							showChevron={showChevron}
							className={triggerClassName}
						/>
					) : (
						<AccountDropBtn
							account={{ name: 'Get Started' }}
							icon='hand-wave'
							active={open}
							collapsed={collapsed}
							density={density}
							showChevron={showChevron}
							className={triggerClassName}
						/>
					)
				}
			/>
			<DropdownMenuContent
				align='start'
				side={side as 'top' | 'right' | 'bottom' | 'left'}
				sideOffset={8}
				className={rootClassName}>
				{workspace.workspace.id ? (
					<DropdownMenuView key={view} direction={direction}>
						{view === 'about' && <AccountDropAbout {...props} />}
						{view === 'appearance' && <AccountDropAppearance onPrev={onBack} />}
						{view === 'community' && <AccountDropCommunity onPrev={onBack} />}
						{view === 'developers' && <AccountDropDevelopers target={target} onPrev={onBack} />}
						{view === 'resources' && <AccountDropResources target={target} onPrev={onBack} />}
						{view === 'select' && (
							<AccountDropSelect
								{...props}
								workspace={workspace}
								onCreate={onCreateWorkspace ? onCreate : undefined}
								onPrev={onBack}
							/>
						)}
						{view === 'support' && <AccountDropSupport target={target} onPrev={onBack} />}
					</DropdownMenuView>
				) : (
					<DropdownMenuView key={view} direction={direction}>
						{view === 'about' && <GuestDropAbout {...props} />}
						{view === 'appearance' && <AccountDropAppearance onPrev={onBack} />}
						{view === 'community' && <AccountDropCommunity onPrev={onBack} />}
						{view === 'developers' && <AccountDropDevelopers target={target} onPrev={onBack} />}
						{view === 'resources' && <AccountDropResources target={target} onPrev={onBack} />}
						{view === 'support' && <AccountDropSupport target={target} onPrev={onBack} />}
					</DropdownMenuView>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
export { WorkspaceMenu };
